import React, { useEffect, useMemo, useState } from "react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";

/* Reflection Deck pink palette (kept local to this page) */
const C = {
    cream: "#FBF6F1",
    blush: "#F4D6DE",
    tearose: "#C68FA2",
    berry: "#9B1B4B",
    midnight: "#7D2F3F",
    gold: "#D3C187",
    charcoal: "#2C2825",
    taupe: "#8A7968",
    ivory: "#FFFCF8",
};

/* 22 Major Arcana — each with 5 reflection prompts */
const MAJORS = [
    { n: "0", name: "The Fool", prompts: [
        "What new beginning is asking for my willingness right now?",
        "Where am I being invited to step before I have all the answers?",
        "What would I do today if I trusted the journey itself?",
        "What does 'lightness' look like in my current chapter?",
        "What am I being asked to release in order to take this step?",
    ]},
    { n: "I", name: "The Magician", prompts: [
        "What tools and resources are already at my disposal?",
        "Where do I keep waiting for permission I do not actually need?",
        "If I trusted my own capability, what would I begin this week?",
        "What truth wants to be spoken aloud rather than only thought?",
        "What am I creating right now, even quietly?",
    ]},
    { n: "II", name: "The High Priestess", prompts: [
        "What does my inner voice already know that the noisy parts are drowning out?",
        "Where am I being asked to listen rather than act?",
        "What question is sitting underneath the question I keep asking?",
        "What does my body know about this situation before my mind does?",
        "What ritual of quiet would help me hear myself this week?",
    ]},
    { n: "III", name: "The Empress", prompts: [
        "Where in my life is abundance asking to be received, not earned?",
        "What does nourishment actually look like for me right now?",
        "How can I tend to my body this week as if it mattered as much as it does?",
        "What am I being asked to grow without forcing?",
        "Where can I let softness be the strategy?",
    ]},
    { n: "IV", name: "The Emperor", prompts: [
        "Where is structure liberating me, and where is it confining me?",
        "What boundary is asking to be set with respect rather than anger?",
        "What is my honest relationship to authority — my own and others'?",
        "Where am I being asked to lead, even quietly?",
        "What foundation am I building, and is it the one I want?",
    ]},
    { n: "V", name: "The Hierophant", prompts: [
        "Which inherited beliefs am I being asked to question?",
        "Which traditions are still nourishing me, and which have I outgrown?",
        "Where am I seeking outside permission for an inside knowing?",
        "What teaching wants to come through me right now?",
        "How can I honor lineage without being chained by it?",
    ]},
    { n: "VI", name: "The Lovers", prompts: [
        "Where am I being asked to make a choice from values, not fear?",
        "What relationship in my life is mirroring me most clearly right now?",
        "Where am I asked to integrate two parts of myself I have kept separate?",
        "What does true partnership ask of me?",
        "What promise am I making, even silently?",
    ]},
    { n: "VII", name: "The Chariot", prompts: [
        "What direction am I committed to, even when the way is unclear?",
        "Where am I being asked to harness opposing forces in myself?",
        "What discipline would actually free me?",
        "Where is willpower the right tool — and where is it the wrong one?",
        "What does steady forward movement look like this week?",
    ]},
    { n: "VIII", name: "Strength", prompts: [
        "What part of me am I afraid of, that is asking only to be met?",
        "Where can I lead with patience instead of force?",
        "What is the wildness in me that wants to be witnessed rather than tamed?",
        "Where am I trying to muscle through something that asks for tenderness?",
        "What does soft strength look like in my life today?",
    ]},
    { n: "IX", name: "The Hermit", prompts: [
        "What does my soul need that only solitude can offer?",
        "What lamp am I carrying for myself — or for someone else?",
        "Where is the path forward asking me to slow down rather than speed up?",
        "What inner counsel is asking to be heard this week?",
        "What would 'sacred retreat' look like in a busy life?",
    ]},
    { n: "X", name: "Wheel of Fortune", prompts: [
        "Where in my life is the wheel turning, and what is my stance toward it?",
        "What am I holding too tightly that the change is asking me to release?",
        "What unexpected door is quietly opening right now?",
        "How can I trust the cycle without trying to control it?",
        "What am I being invited to receive rather than chase?",
    ]},
    { n: "XI", name: "Justice", prompts: [
        "Where am I being asked to tell the whole truth — even quietly, to myself?",
        "What action of mine is asking to be accounted for with honesty?",
        "Where am I out of integrity with my own values?",
        "What balance is asking to be restored in my life?",
        "What does 'fair' look like to me, beyond what others expect?",
    ]},
    { n: "XII", name: "The Hanged Man", prompts: [
        "What am I being asked to surrender, not as defeat but as wisdom?",
        "What new perspective is available if I stop forcing the old one?",
        "Where is stillness the most powerful move I can make?",
        "What story have I been holding upside down — and how does it look now?",
        "What does sacred pause make possible?",
    ]},
    { n: "XIII", name: "Death", prompts: [
        "What chapter is asking to be honored and laid to rest?",
        "What part of me is being asked to evolve, not be erased?",
        "What strategy have I outgrown but still cling to?",
        "Where is rebirth quietly waiting, just beyond a goodbye?",
        "What does grief have to teach me right now?",
    ]},
    { n: "XIV", name: "Temperance", prompts: [
        "Where am I being asked to blend rather than choose?",
        "What two parts of my life are asking for integration?",
        "Where can I bring more patience to a place I have rushed?",
        "What does 'right measure' look like for me this season?",
        "How can I let healing happen at its own pace this week?",
    ]},
    { n: "XV", name: "The Devil", prompts: [
        "What chains am I treating as locked when they have always been loose?",
        "What attachment is costing me something I value more?",
        "Where am I trading freedom for the familiar?",
        "What appetite of mine is asking to be honored, not feared?",
        "What would liberation, in a small everyday way, look like?",
    ]},
    { n: "XVI", name: "The Tower", prompts: [
        "What false structure has already begun to fall?",
        "What truth is the rupture trying to reveal?",
        "Where am I being asked to stop pretending?",
        "What is the most honest next step, even if it is small?",
        "What is more freeing in its ending than it was in its keeping?",
    ]},
    { n: "XVII", name: "The Star", prompts: [
        "What hope is asking to be quietly tended, not announced?",
        "Where is healing already happening, even if I cannot yet feel it?",
        "What faith am I being invited to renew?",
        "What small light am I being asked to follow this week?",
        "Where is the dust settling, and what beauty is underneath it?",
    ]},
    { n: "XVIII", name: "The Moon", prompts: [
        "What is being illuminated that I would rather not see?",
        "Where am I being asked to trust intuition over evidence?",
        "What old wound is rising for tending, not punishment?",
        "What dream or image is repeating, and what might it know?",
        "How can I walk through this uncertainty kindly?",
    ]},
    { n: "XIX", name: "The Sun", prompts: [
        "What in my life is asking to be celebrated, even briefly?",
        "Where am I being invited into joy, without guilt?",
        "What part of me is finally ready to be seen?",
        "What does childlike clarity offer me today?",
        "Where is warmth — given or received — the right next move?",
    ]},
    { n: "XX", name: "Judgement", prompts: [
        "What call am I hearing that I have been quietly ignoring?",
        "What old self is asking to be released with respect?",
        "What is asking to be forgiven — by me, of me, or both?",
        "What awakening is unfolding right now?",
        "What chapter am I being invited to begin with clearer eyes?",
    ]},
    { n: "XXI", name: "The World", prompts: [
        "What cycle am I completing, even if I have not named it yet?",
        "Where is integration possible — bringing all parts of me to the table?",
        "What does 'wholeness' mean to me right now, scars included?",
        "How do I want to celebrate this arrival, however quiet?",
        "What new beginning is waiting on the other side of this completion?",
    ]},
];

/* tiny seeded shuffle so the deck order changes each visit */
const shuffle = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const CardBack = () => (
    <svg viewBox="0 0 60 100" width="100%" height="100%" aria-hidden style={{ display: "block" }}>
        <defs>
            <linearGradient id="jpBack" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#9C3550" />
                <stop offset="100%" stopColor="#7D2F3F" />
            </linearGradient>
        </defs>
        <rect x="2" y="2" width="56" height="96" rx="4" fill="url(#jpBack)" stroke="#5A1F2C" strokeWidth="0.6" />
        <rect x="5" y="5" width="50" height="90" rx="3" fill="none" stroke="#D3C187" strokeWidth="0.6" opacity="0.7" />
        <circle cx="30" cy="50" r="10" fill="none" stroke="#D3C187" strokeWidth="0.6" opacity="0.8" />
        <circle cx="30" cy="50" r="5" fill="none" stroke="#D3C187" strokeWidth="0.5" opacity="0.8" />
        <text x="6" y="14" fontSize="6" fill="#D3C187" opacity="0.7" fontFamily="serif">✦</text>
        <text x="50" y="94" fontSize="6" fill="#D3C187" opacity="0.7" fontFamily="serif">✦</text>
    </svg>
);

const CardFace = ({ card }) => (
    <svg viewBox="0 0 60 100" width="100%" height="100%" aria-hidden style={{ display: "block" }}>
        <rect x="2" y="2" width="56" height="96" rx="4" fill="#FFFCF8" stroke="#C9B996" strokeWidth="0.7" />
        <rect x="5" y="5" width="50" height="90" rx="3" fill="none" stroke="#C68FA2" strokeWidth="0.5" opacity="0.7" />
        <text x="30" y="20" fontSize="8" fill="#7D2F3F" textAnchor="middle" fontFamily="'Cormorant Garamond', serif" fontStyle="italic">{card.n}</text>
        <circle cx="30" cy="50" r="14" stroke="#C68FA2" strokeWidth="0.6" fill="#F4D6DE" opacity="0.55" />
        <text x="30" y="55" fontSize="10" fill="#7D2F3F" textAnchor="middle" fontFamily="'Cormorant Garamond', serif">{card.n}</text>
        <text x="30" y="88" fontSize="4.4" fill="#7D2F3F" textAnchor="middle" fontFamily="'DM Sans', sans-serif" letterSpacing="0.6">{card.name.toUpperCase()}</text>
        <text x="6" y="14" fontSize="4.5" fill="#D3C187" opacity="0.85" fontFamily="serif">✦</text>
        <text x="50" y="94" fontSize="4.5" fill="#D3C187" opacity="0.85" fontFamily="serif">✦</text>
    </svg>
);

const Deck = ({ deck, onPull, pulled }) => {
    // Fan out the deck in a soft arc
    const N = deck.length;
    return (
        <div
            data-testid="journal-pull-deck"
            className="relative mx-auto"
            style={{ width: "100%", maxWidth: 980, height: 360 }}
        >
            {deck.map((card, i) => {
                const t = (i - (N - 1) / 2) / ((N - 1) / 2);
                const angle = t * 26; // -26° to +26°
                const x = t * 380;    // px horizontal spread
                const y = Math.abs(t) * 28; // slight rise on outer cards
                const isPulled = pulled && pulled.n === card.n;
                return (
                    <button
                        key={card.n}
                        type="button"
                        onClick={() => onPull(card)}
                        data-testid={`journal-pull-card-${card.n}`}
                        aria-label={`Pull a card`}
                        className="absolute transition-all duration-700"
                        style={{
                            left: "50%",
                            top: 40,
                            width: 92,
                            height: 152,
                            transform: `translate(-50%, ${y}px) translateX(${x}px) rotate(${angle}deg) ${isPulled ? "translateY(-22px) scale(1.06)" : ""}`,
                            transformOrigin: "center bottom",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            zIndex: 30 + Math.round(50 - Math.abs(t) * 50),
                            filter: pulled && !isPulled ? "saturate(0.6) brightness(0.96)" : "none",
                            opacity: pulled && !isPulled ? 0.6 : 1,
                        }}
                    >
                        {isPulled ? (
                            <div style={{ width: "100%", height: "100%", pointerEvents: "none" }}>
                                <CardFace card={card} />
                            </div>
                        ) : (
                            <div style={{ width: "100%", height: "100%", pointerEvents: "none" }}>
                                <CardBack />
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

export default function JournalPullPage() {
    const [deck, setDeck] = useState(() => shuffle(MAJORS));
    const [pulled, setPulled] = useState(null);
    const [shuffled, setShuffled] = useState(false);

    // Soft entrance: cards stack center first, then spread on mount
    useEffect(() => {
        const t = setTimeout(() => setShuffled(true), 250);
        return () => clearTimeout(t);
    }, []);

    const reshuffle = () => {
        setPulled(null);
        setShuffled(false);
        setTimeout(() => {
            setDeck(shuffle(MAJORS));
            setShuffled(true);
        }, 200);
    };

    const prompts = useMemo(() => (pulled ? pulled.prompts : []), [pulled]);

    return (
        <main data-testid="journal-pull-page" style={{ background: C.cream, color: C.charcoal }}>
            <Seo
                title="The Journal Pull | Free Interactive Tarot Reflection | The Reflection Deck"
                description="A free, on-the-house tarot experience. Pull a card from the Major Arcana and receive five journal prompts shaped to its themes — no booking, no payment."
            />
            <Nav />

            <section className="relative" style={{ background: C.cream, padding: "112px 24px 24px" }}>
                <div className="mx-auto text-center" style={{ maxWidth: 720 }}>
                    <span
                        style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 10,
                            letterSpacing: "0.34em",
                            textTransform: "uppercase",
                            color: C.taupe,
                        }}
                    >
                        Free · Interactive
                    </span>
                    <h1
                        className="mt-5"
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontWeight: 500,
                            fontSize: "clamp(34px, 4vw, 56px)",
                            lineHeight: 1.1,
                            color: C.charcoal,
                        }}
                    >
                        The Journal{" "}
                        <em style={{ fontStyle: "italic", color: C.berry, fontWeight: 500 }}>
                            Pull.
                        </em>
                    </h1>
                    <p
                        className="mt-5 mx-auto"
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontStyle: "italic",
                            fontSize: 18,
                            color: C.taupe,
                            lineHeight: 1.55,
                            maxWidth: 540,
                        }}
                    >
                        Choose one card from the spread. Five journal prompts will be waiting — shaped to the
                        card you pulled, ready for whichever one finds you.
                    </p>
                </div>
            </section>

            <section className="relative" style={{ background: C.cream, padding: "32px 24px 80px" }}>
                <div
                    className="mx-auto"
                    style={{
                        maxWidth: 1080,
                        opacity: shuffled ? 1 : 0,
                        transform: shuffled ? "translateY(0)" : "translateY(12px)",
                        transition: "all 700ms ease",
                    }}
                >
                    <Deck deck={deck} onPull={setPulled} pulled={pulled} />
                </div>

                <div className="text-center mt-2">
                    <button
                        type="button"
                        onClick={reshuffle}
                        data-testid="journal-pull-shuffle"
                        style={{
                            background: "transparent",
                            color: C.midnight,
                            border: `1px solid ${C.midnight}`,
                            padding: "12px 26px",
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 11,
                            letterSpacing: "0.3em",
                            textTransform: "uppercase",
                            fontWeight: 500,
                            cursor: "pointer",
                        }}
                    >
                        {pulled ? "Pull Another" : "Reshuffle the Deck"}
                    </button>
                </div>
            </section>

            {pulled && (
                <section
                    data-testid="journal-pull-prompts"
                    style={{ background: C.blush, padding: "80px 24px 100px" }}
                >
                    <div className="mx-auto text-center" style={{ maxWidth: 780 }}>
                        <span
                            style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: 10,
                                letterSpacing: "0.34em",
                                textTransform: "uppercase",
                                color: C.midnight,
                            }}
                        >
                            You drew · {pulled.n}
                        </span>
                        <h2
                            className="mt-4"
                            style={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontWeight: 500,
                                fontSize: "clamp(28px, 3.4vw, 44px)",
                                color: C.charcoal,
                            }}
                        >
                            {pulled.name}
                        </h2>
                        <p
                            className="mt-3"
                            style={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontStyle: "italic",
                                fontSize: 16,
                                color: C.midnight,
                                opacity: 0.85,
                            }}
                        >
                            Five prompts to sit with — pick one or work through all five at your own pace.
                        </p>

                        <ol
                            className="mt-10 mx-auto text-left space-y-5"
                            style={{ maxWidth: 620 }}
                            data-testid="journal-pull-prompts-list"
                        >
                            {prompts.map((p, i) => (
                                <li
                                    key={i}
                                    style={{
                                        background: C.ivory,
                                        borderLeft: `3px solid ${C.berry}`,
                                        padding: "18px 22px",
                                        fontFamily: "'Cormorant Garamond', serif",
                                        fontSize: 19,
                                        lineHeight: 1.55,
                                        color: C.charcoal,
                                    }}
                                >
                                    <span style={{ color: C.berry, fontStyle: "italic", marginRight: 10 }}>
                                        {i + 1}.
                                    </span>
                                    {p}
                                </li>
                            ))}
                        </ol>

                        <p
                            className="mt-12 mx-auto"
                            style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: 12,
                                lineHeight: 1.8,
                                color: C.taupe,
                                fontStyle: "italic",
                                maxWidth: 560,
                            }}
                        >
                            This is a self-guided reflection tool, not therapy or clinical advice. If
                            something heavy surfaces, please reach out for proper support.
                        </p>
                    </div>
                </section>
            )}

            <Footer />
        </main>
    );
}
